import {
  CircleOutline,
  DocumentBlank,
  FlowConnection,
  TableSplit,
} from '@carbon/icons-react';
import { createSelector } from '@reduxjs/toolkit';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { alphaSort } from 'vet-bones/utils';

import { TreeNodeProps } from 'src/components/tree/TreeNode';
import { PROJECT_MAPPING_EXPAND_MAPPINGS_PARAM } from 'src/constants/project';
import { getProjectSidebarMappingItemTestId } from 'src/constants/testIds';
import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { isMapping } from 'src/containers/ProjectDiagram/utils/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { getMappingTargetIri } from 'src/containers/ProjectDiagram/utils/mapping/getMappingTargetIri';
import {
  getResourceKey,
  getResourceValue,
} from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { ProjectSidebarMappingMenu } from 'src/containers/ProjectSidebar/components/ProjectSidebarMappingMenu';
import { projectSelector } from 'src/selectors/project';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectLinkDataMatchSelector } from 'src/selectors/projectLinkDataMatch';
import { projectNodeDataArraySelector } from 'src/selectors/projectNodeDataArray';
import { projectResourcesByLabelSelector } from 'src/selectors/projectResource';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { getProjectMappingRoute } from 'src/utils/routes/getProjectMappingRoute';
import { filterEmptyNodes, findMapNode, forEachNode } from 'src/utils/tree';

import styles from 'src/containers/ProjectSidebar/projectSidebar.module.scss';

type MappingNode = TreeNodeProps<{ mappingId?: string }>;

export const resourceTreeSelector = (
  projectId: string,
  expandedNodeIds: Record<string, boolean> = {},
  selectedMappingId?: string
) =>
  createSelector(
    projectSelector(projectId),
    projectLabelsByIriSelector(projectId),
    projectLinkDataMatchSelector(projectId, { category: LinkCategory.MAPPING }),
    projectResourcesByLabelSelector(projectId),
    projectNodeDataArraySelector(projectId),
    (project, labelsByIri, linkData, resources, nodeDataArray) =>
      resources
        .map<MappingNode>((resource) => {
          const mappingsByModelConcept: Record<
            string,
            Array<{ label: string; mappingId: string }>
          > = {};
          const resourceKey = getResourceKey(resource.id);
          (linkData as Mapping[]).forEach((link) => {
            if (link.from === resourceKey && link.to) {
              const iri = getMappingTargetIri(link.to, nodeDataArray);
              if (!iri) {
                return;
              }

              if (!mappingsByModelConcept[iri]) {
                mappingsByModelConcept[iri] = [];
              }
              mappingsByModelConcept[iri].push({
                label: link.text,
                mappingId: link.value,
              });
            }
          });

          return {
            // Level 1: Resources
            id: resource.id,
            childNodes: Object.entries(mappingsByModelConcept)
              .map<MappingNode>(([modelConceptIri, mappings]) => {
                // Level 2: Model Concepts
                const modelConceptNodeId = `${resource.id}-${modelConceptIri}`;
                return {
                  id: modelConceptNodeId,
                  childNodes: mappings
                    .map<MappingNode>(({ label, mappingId }) => {
                      // Level 3: Mappings
                      const id = `${modelConceptNodeId}-${mappingId}`;
                      const isSelected = selectedMappingId === mappingId;
                      return {
                        id,
                        mappingId,
                        menuItemProps: {
                          className:
                            styles.projectSidebarMappingsMappingMenuItem,
                          leftIcon: FlowConnection,
                          rightElement: (
                            <ProjectSidebarMappingMenu mappingId={mappingId} />
                          ),
                          active: isSelected,
                          testId: getProjectSidebarMappingItemTestId(mappingId),
                          text: label,
                        },
                      };
                    })
                    .sort(alphaSort((item) => item.menuItemProps.text || '')),
                  hasCaret: true,
                  isExpanded: expandedNodeIds[modelConceptNodeId] ?? false,
                  menuItemProps: {
                    leftIcon: CircleOutline,
                    testId: getProjectSidebarMappingItemTestId(modelConceptIri),
                    text: getLabelForIri(labelsByIri, modelConceptIri),
                  },
                };
              })
              .filter(filterEmptyNodes)
              .sort(alphaSort((item) => item.menuItemProps.text || '')),
            hasCaret: true,
            isExpanded: expandedNodeIds[resource.id] ?? false,
            menuItemProps: {
              leftIcon: project?.virtualGraphsById[resource.id]
                ? TableSplit
                : DocumentBlank,
              testId: getProjectSidebarMappingItemTestId(resource.id),
              text: resource.label,
            },
          };
        })
        .sort(alphaSort((item) => item.menuItemProps.text || ''))
        .filter(filterEmptyNodes)
  );

export const useMappingsTree = (filter: string) => {
  const { projectId, mappingId } = useRouterParams<'PROJECT_MAPPING'>();
  const diagram = useDiagram();
  const history = useHistory();

  const [expandedNodeIds, setExpandedNodeIds] = React.useState<
    Record<string, boolean>
  >(() => {
    if (!diagram) {
      return {};
    }
    const { model } = diagram;
    const connectedMappings = model.linkDataArray.filter(
      (linkData): linkData is Mapping & { to: string } =>
        isMapping(linkData) && Boolean(linkData.to)
    );
    return connectedMappings.reduce<Record<string, boolean>>((acc, mapping) => {
      const resourceValue = getResourceValue(mapping.from);
      const modelConceptValue = getMappingTargetIri(
        mapping.to,
        model.nodeDataArray
      );

      if (!mappingId || mappingId === mapping.value) {
        acc[resourceValue] = true;
        if (modelConceptValue) {
          acc[`${resourceValue}-${modelConceptValue}`] = true;
        }
      } else if (modelConceptValue) {
        acc[`${resourceValue}-${modelConceptValue}`] = true;
      }

      return acc;
    }, {});
  });
  const prevExpandedNodeIds =
    React.useRef<Record<string, boolean>>(expandedNodeIds);

  const didFilterChange = useDidValueChange(filter);
  React.useEffect(() => {
    if (didFilterChange && !filter) {
      setExpandedNodeIds(prevExpandedNodeIds.current);
    }
  }, [didFilterChange, filter]);

  React.useEffect(() => {
    if (!filter) {
      prevExpandedNodeIds.current = expandedNodeIds;
    }
  }, [expandedNodeIds, filter]);

  const unfilteredTree = useReduxSelector(
    resourceTreeSelector(projectId, expandedNodeIds, mappingId)
  );

  const expandSelectedMappingId = React.useCallback(() => {
    if (!mappingId) {
      return;
    }

    const info = findMapNode(unfilteredTree, (node, parentNodes) =>
      mappingId === node.mappingId ? { node, parentNodes } : undefined
    );
    if (info) {
      setExpandedNodeIds((prevIds) => ({
        ...prevIds,
        ...Object.fromEntries(info.parentNodes.map(({ id }) => [id, true])),
      }));
    }
  }, [mappingId, unfilteredTree]);

  // Handle mappingId updates
  const didMappingIdChange = useDidValueChange(mappingId);
  React.useEffect(() => {
    if (didMappingIdChange) {
      expandSelectedMappingId();
    }
  }, [didMappingIdChange, expandSelectedMappingId]);

  const allExpandedNodeIds = React.useMemo(() => {
    const paths: Record<string, boolean> = {};
    forEachNode(unfilteredTree, (node) => {
      paths[node.id] = true;
    });
    return paths;
  }, [unfilteredTree]);

  React.useEffect(() => {
    const { search, pathname } = history.location;
    const params = new URLSearchParams(search);

    if (params.has(PROJECT_MAPPING_EXPAND_MAPPINGS_PARAM)) {
      setExpandedNodeIds(allExpandedNodeIds);
      history.replace(pathname);
    }
  }, [history, allExpandedNodeIds]);

  const onNodeClick = React.useCallback(
    (node: MappingNode) => {
      const { mappingId } = node;
      if (mappingId) {
        history.push(getProjectMappingRoute(projectId, mappingId));
        return;
      }

      setExpandedNodeIds((prevPaths) => ({
        ...prevPaths,
        [node.id]: !node.isExpanded,
      }));
    },
    [history, projectId]
  );

  const { allCount, filteredTree, itemsCount } = React.useMemo(() => {
    const trimmed = filter.trim().toLowerCase();
    const isMatch = (node: MappingNode) =>
      !trimmed || node.menuItemProps.text?.toLowerCase().includes(trimmed);

    let allCount = 0;
    let itemsCount = 0;
    const filteredTree = unfilteredTree
      .map((resource) => {
        const hasResourceMatch = isMatch(resource);
        return {
          ...resource,
          childNodes: resource.childNodes
            ?.map((modelConcept) => {
              const hasModelConceptMatch = isMatch(modelConcept);
              return {
                ...modelConcept,
                childNodes: modelConcept.childNodes?.filter((node) => {
                  allCount += 1;
                  if (
                    hasResourceMatch ||
                    hasModelConceptMatch ||
                    isMatch(node)
                  ) {
                    itemsCount += 1;
                    return true;
                  }
                  return false;
                }),
              };
            })
            .filter(filterEmptyNodes),
        };
      })
      .filter(filterEmptyNodes);

    return { filteredTree, allCount, itemsCount };
  }, [filter, unfilteredTree]);

  return { allCount, filteredTree, itemsCount, onNodeClick };
};
